import { Link } from "react-router-dom";
import {
  Link as LinkIcon,
  FileText,
  Calendar,
  BarChart3,
  QrCode,
  Users,
  Sparkles,
  ArrowRight,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";

type Feature = {
  icon: typeof LinkIcon;
  iconClass: string;
  boxClass: string;
  title: string;
  tagline: string;
  bullets: string[];
  aiTag?: string;
};

const features: Feature[] = [
  {
    icon: LinkIcon,
    iconClass: "text-primary",
    boxClass: "bg-primary/10",
    title: "Bio link builder",
    tagline: "Your Linktree alternative.",
    bullets: ["Multi-language pages", "Link scheduling", "Ready-made templates"],
  },
  {
    icon: FileText,
    iconClass: "text-violet-400",
    boxClass: "bg-violet-500/10",
    title: "Smart forms",
    tagline: "Collect leads, scored instantly.",
    bullets: ["Drag-and-drop builder", "AI scores leads 1–5 stars", "Auto-route hot leads"],
    aiTag: "AI Lead Scoring",
  },
  {
    icon: Calendar,
    iconClass: "text-emerald-400",
    boxClass: "bg-emerald-500/10",
    title: "Booking & AI brief",
    tagline: "Show up prepared, every time.",
    bullets: ["Online booking page", "AI guest brief 30 min before", "Google Calendar sync"],
    aiTag: "AI Pre-Brief",
  },
  {
    icon: BarChart3,
    iconClass: "text-amber-400",
    boxClass: "bg-amber-500/10",
    title: 'Analytics + So What?',
    tagline: "Data with a next step.",
    bullets: ["Clicks, devices, referrers", "AI tells you what to fix", "Weekly trend digest"],
    aiTag: "AI Insights",
  },
  {
    icon: QrCode,
    iconClass: "text-primary",
    boxClass: "bg-primary/10",
    title: "QR code generator",
    tagline: "Branded QR, tracked scans.",
    bullets: ["Logo & color presets", "Custom patterns", "Real-time scan analytics"],
  },
  {
    icon: Users,
    iconClass: "text-violet-400",
    boxClass: "bg-violet-500/10",
    title: "Agency workspace",
    tagline: "Run every client from one place.",
    bullets: ["AI health scores", "One-click client reports", "Team collaboration"],
    aiTag: "AI Health Score",
  },
];

const flywheel = [
  { label: "Collect", sub: "Bio, Forms, Booking", boxClass: "bg-primary/10 text-primary" },
  { label: "Analyze", sub: "Score, Insights", boxClass: "bg-violet-500/10 text-violet-400" },
  { label: "Act", sub: "Brief, Follow-up", boxClass: "bg-emerald-500/10 text-emerald-400" },
  { label: "Grow", sub: "Reports, Health", boxClass: "bg-amber-500/10 text-amber-400" },
];

const thaiCards: { title: string; bullets: string[] }[] = [
  {
    title: "PromptPay QR",
    bullets: ["สร้าง QR พร้อมเพย์ทันที", "ลูกค้าสแกนจ่ายได้เลย", "ติดตามยอดทุกบิล"],
  },
  {
    title: "LINE OA Alerts",
    bullets: ["แจ้งเตือนทุกการจอง", "แจ้ง lead ใหม่ทันที", "ส่งเข้า LINE ที่ใช้ทุกวัน"],
  },
  {
    title: "ภาษาไทย + English",
    bullets: ["Bio page สองภาษา", "AI insights ภาษาไทย", "สลับภาษาอัตโนมัติ"],
  },
];

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((b) => (
        <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground leading-snug">
          <Check className="h-4 w-4 text-foreground/60 mt-0.5 shrink-0" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

export default function FeaturesSection() {
  return (
    <>
      {/* Section A — Feature Grid */}
      <section id="features" className="max-w-5xl mx-auto px-4 py-24 sm:py-32">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <span className="inline-block text-xs font-medium tracking-wider uppercase text-primary mb-3">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 leading-tight">
            Everything you need.{" "}
            <span className="text-primary">AI handles the rest.</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            8 tools + 7 AI features — all connected in one workspace.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <article
              key={f.title}
              className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-colors flex flex-col"
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${f.boxClass}`}>
                <f.icon className={`h-5 w-5 ${f.iconClass}`} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1">{f.title}</h3>
              <p className="text-sm text-foreground/80 mb-4 font-medium">{f.tagline}</p>
              <BulletList items={f.bullets} />
              {f.aiTag && (
                <span className="inline-flex items-center gap-1 mt-4 self-start bg-violet-500/10 text-violet-400 text-xs px-2.5 py-1 rounded-full">
                  <Sparkles className="h-3 w-3" />
                  {f.aiTag}
                </span>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Section B — AI Flywheel */}
      <section className="max-w-5xl mx-auto px-4 py-24 sm:py-32">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <span className="inline-block text-xs font-medium tracking-wider uppercase text-violet-400 mb-3">
            How it works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 leading-tight">
            AI that learns from{" "}
            <span className="text-primary">every interaction.</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Every tool feeds AI. Every insight makes your tools smarter.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5">
          {flywheel.map((step, i) => (
            <div key={step.label} className="flex items-center gap-3 sm:gap-5">
              <div className={`${step.boxClass} rounded-2xl px-5 py-4 text-center min-w-[110px]`}>
                <div className="text-sm font-semibold">{step.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{step.sub}</div>
              </div>
              {i < flywheel.length - 1 && (
                <ArrowRight className="h-5 w-5 text-muted-foreground/40 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Section C — Thai Market */}
      <section className="max-w-5xl mx-auto px-4 py-24 sm:py-32">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <span className="inline-block text-xs font-medium tracking-wider uppercase text-emerald-400 mb-3">
            Made in Thailand
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 leading-tight">
            สร้างมาเพื่อ <span className="text-primary">SME ไทย</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            PromptPay, LINE OA, ภาษาไทย — ฟีเจอร์ที่ต่างชาติไม่มี
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {thaiCards.map((c) => (
            <article
              key={c.title}
              className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 transition-colors"
            >
              <h3 className="text-lg font-semibold text-foreground mb-4">{c.title}</h3>
              <BulletList items={c.bullets} />
            </article>
          ))}
        </div>
      </section>

      {/* Section D — Closing CTA */}
      <section className="max-w-3xl mx-auto px-4 py-24 sm:py-32 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-5 leading-tight">
          Ready to let AI <span className="text-primary">run your marketing?</span>
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
          Free forever for solo. Set up in 2 minutes — no credit card needed.
        </p>
        <Button
          asChild
          size="lg"
          className="rounded-xl px-8 h-12 text-base font-semibold shadow-lg shadow-primary/30 hover:shadow-primary/40 hover:scale-[1.02] transition-all w-full sm:w-auto"
        >
          <Link to="/signup">Start free — no credit card</Link>
        </Button>
      </section>
    </>
  );
}
